import { Image, Space, Tooltip } from 'antd';
import { forwardRef, memo, useImperativeHandle, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import TableComponent from 'components/Table/TableComponent';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import Content from 'layouts/Content';
import { EyeIcon } from 'assets/svg';
import formatNumber, { formatDate } from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import { HocChangePagination } from 'utils/HocChangePagination';
import { ProductData } from './ProductSlocListPage';
import { StorageLocation } from 'apis/GraphQL/Variables/product';
import useQueryParam from 'hooks/useQueryPrams';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import path from 'routes/Path';

const { FileImageOutlined } = icons;

interface IProps {
  data: ProductData | undefined;
  isLoading: boolean;
  refetchFilter: (filter: any) => void;
  filterData: Partial<StorageLocation>;
}

const TableProductSloc = memo(
  forwardRef(({ data, isLoading, refetchFilter, filterData }: IProps, ref) => {
    const navigate = useNavigate();
    const [imageError, setImageError] = useState<Record<string, boolean>>({});

    const queryParam = useQueryParam(),
      page = parseInt(queryParam.get('page') + '') || 1,
      page_size =
        parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

    useImperativeHandle(ref, () => ({
      handleExportExcel,
    }));

    const handleExportExcel = () => {
      refetchFilter({
        ...filterData,
        skip: 0,
        take: data?.pageInfo.totalItems,
        types: 'export',
        headerRow: columns,
      });
    };

    const handleImageError = (id: string) => {
      setImageError((prev) => ({ ...prev, [id]: true }));
    };

    const columns: ColumnsTypeCustom = [
      {
        title: 'STT',
        width: 70,
        align: 'center',
        render: (_, __, index) => index + 1 + page_size * (page - 1),
      },
      {
        title: 'Chi nhánh',
        align: 'center',
        dataIndex: 'plantId',
        width: 100,
        render: (value) => value || '-',
      },
      {
        title: 'Mã sản phẩm',
        align: 'left',
        dataIndex: ['tproducts', 'id'],
        width: 200,
        render: (value) => value || '-',
      },
      {
        title: 'Text scan',
        align: 'left',
        dataIndex: ['tproducts', 'textScan'],
        width: 150,
        render: (value) => value || '-',
      },
      {
        title: 'Hình ảnh',
        width: 100,
        align: 'center',
        render: (record) => {
          const isImageInvalid = !record?.urlImage1 || imageError[record?.id];

          if (isImageInvalid) {
            return (
              <FileImageOutlined
                style={{
                  height: 50,
                  width: 50,
                  fontSize: 24,
                  justifyContent: 'center',
                }}
              />
            );
          }

          return (
            <Image
              style={{ height: 50, width: 50 }}
              src={record?.urlImage1}
              alt={record?.name}
              onError={() => handleImageError(record?.id)}
            />
          );
        },
      },
      {
        title: 'Tên sản phẩm',
        dataIndex: ['tproducts', 'name'],
        align: 'left',
        width: 300,
        render: (value) => value || '-',
      },
      {
        title: 'Số sloc',
        dataIndex: 'sloc',
        align: 'right',
        key: 'sloc',
        width: 100,
        render: (value) => value || '-',
      },
      {
        title: 'Số batch',
        dataIndex: 'batch',
        align: 'right',
        key: 'batch',
        width: 120,
        render: (value) => value || '-',
      },
      {
        title: 'MC Group',
        dataIndex: 'mcGroup',
        align: 'left',
        width: 100,
        render: (value) => value || '-',
      },
      {
        title: 'Số lượng',
        dataIndex: 'totalInventory',
        key: 'totalInventory',
        align: 'right',
        width: 100,
        render: (value) => value || '-',
      },
      {
        title: 'ĐVT',
        dataIndex: ['tproducts', 'unit'],
        align: 'left',
        width: 100,
        render: (value) => value || '-',
      },
      {
        title: 'Tuổi vàng',
        dataIndex: ['tproducts', 'age'],
        align: 'right',
        width: 100,
        render: (value) => value || '-',
      },
      {
        title: 'Loại vàng',
        dataIndex: ['tproducts', 'ttype', 'name'],
        align: 'left',
        width: 130,
        render: (value) => value || '-',
      },
      {
        title: 'TL vàng (chỉ)',
        dataIndex: 'goldenWeightThread',
        key: 'goldenWeightThread',
        align: 'right',
        width: 160,
        render: (value) => value || '-',
      },
      {
        title: 'TL món (chỉ)',
        dataIndex: 'itemWeightThread',
        key: 'itemWeightThread',
        align: 'right',
        width: 160,
        render: (value) => value || '-',
      },
      {
        title: 'TL món (gram)',
        dataIndex: 'itemWeightGram',
        key: 'itemWeightGram',
        align: 'right',
        width: 160,
        render: (value) => value || '-',
      },
      {
        title: 'Nhóm giá (vnđ)',
        dataIndex: 'groupPrice',
        align: 'right',
        width: 160,
        render: (value) => value || '-',
      },
      {
        title: 'Đơn giá bán (vnđ/gram)',
        dataIndex: 'unitPriceByGram',
        key: 'unitPriceByGram',
        align: 'right',
        width: 180,
        render: (value) => formatNumber(value) || '-',
      },
      {
        title: 'Đơn giá bán (vnđ/phân)',
        dataIndex: 'unitPriceByInch',
        align: 'right',
        width: 180,
        render: (value) => formatNumber(value) || '-',
      },
      {
        title: 'Giá công (vnđ)',
        dataIndex: 'processPrice',
        key: 'processPrice',
        align: 'right',
        width: 160,
        render: (value) => formatNumber(value) || '-',
      },
      {
        title: 'Thành tiền',
        dataIndex: 'totalPrice',
        key: 'totalPrice',
        align: 'right',
        width: 160,
        render: (value) => formatNumber(value) || '-',
      },
      {
        title: 'Người tạo',
        dataIndex: 'createName',
        align: 'left',
        width: 100,
        render: (value) => value || '-',
      },
      {
        title: 'Ngày tạo',
        dataIndex: 'createDate',
        align: 'left',
        width: 150,
        type: 'date',
        render: (value) =>
          !value ? '-' : formatDate(value, 'DD/MM/YYYY HH:mm:ss'),
      },
      {
        title: 'Người cập nhật',
        dataIndex: 'modifyName',
        align: 'left',
        width: 150,
        render: (value) => value || '-',
      },
      {
        title: 'Ngày cập nhật',
        dataIndex: 'modifyDate',
        align: 'left',
        width: 150,
        type: 'date',
        render: (value) => formatDate(value, 'DD/MM/YYYY HH:mm:ss'),
      },
      {
        title: 'Thao tác',
        fixed: 'right',
        width: 100,
        align: 'center',
        render: (_: any, record: any) => (
          <Space size={'middle'} className="text-lg">
            <Tooltip placement="top" title={'Xem chi tiết'}>
              <EyeIcon
                onClick={() =>
                  navigate(`${path.PRODUCT_DETAIL}`, {
                    state: record,
                  })
                }
                className="cursor-pointer"
              />
            </Tooltip>
          </Space>
        ),
      },
    ];

    return (
      <Content>
        <TableComponent
          dataSource={data?.items}
          columns={columns}
          loading={isLoading}
          scroll={{ x: 1500 }}
          pagination={{
            current: data?.pageInfo?.currentPage,
            pageSize: data?.pageInfo?.itemsPerPage,
            total: data?.pageInfo?.totalItems,
            onChange: HocChangePagination(),
          }}
        />
      </Content>
    );
  }),
);

export default TableProductSloc;
